import React, { useState } from "react";
import { toast } from "sonner";
import { Link2, Check, ImageDown } from "lucide-react";
import { incidentUrl } from "../lib/focusIncident";
import { downloadCover } from "../lib/cover";

const XIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2H21.5l-7.5 8.57L22.8 22h-6.9l-5.4-7.06L4.3 22H1l8.02-9.17L1.2 2h7.07l4.88 6.45L18.24 2Zm-1.2 18h1.9L7.03 3.9H5.0L17.04 20Z" />
  </svg>
);

const RedditIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M22 12.1a2.2 2.2 0 0 0-3.7-1.6 10.8 10.8 0 0 0-5.8-1.8l1-4.6 3.2.7a1.6 1.6 0 1 0 .2-1l-3.6-.8a.5.5 0 0 0-.6.4l-1.1 5.3a10.9 10.9 0 0 0-5.9 1.8 2.2 2.2 0 1 0-2.4 3.6 4.3 4.3 0 0 0 0 .7c0 3.4 4 6.2 8.8 6.2s8.8-2.8 8.8-6.2a4.3 4.3 0 0 0 0-.7 2.2 2.2 0 0 0 1.1-2ZM7 13.6a1.6 1.6 0 1 1 3.2 0 1.6 1.6 0 0 1-3.2 0Zm9 4.3c-1.1 1.1-3.2 1.2-3.9 1.2s-2.8-.1-3.9-1.2a.4.4 0 0 1 .6-.6c.7.7 2.2.9 3.3.9s2.6-.2 3.3-.9a.4.4 0 0 1 .6.6Zm-.3-2.7a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2Z" />
  </svg>
);

export const ShareButtons = ({ incident }) => {
  const [copied, setCopied] = useState(false);
  const [rendering, setRendering] = useState(false);
  const url = incidentUrl(incident.id);
  const text = `Exhibit: "${incident.title}" — Cringe ${incident.cringe}/10. Jack Doherty Hall of Shame`;

  const cover = async () => {
    if (rendering) return;
    setRendering(true);
    try {
      await downloadCover(incident);
      toast.success("COVER PRINTED. POST IT EVERYWHERE.");
    } catch (e) {
      console.error("cover error", e);
      toast.error("PRINTER JAMMED. TRY AGAIN.");
    } finally {
      setRendering(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    toast.success("LINK COPIED. GO RUIN SOMEONE'S FEED.");
    setTimeout(() => setCopied(false), 1500);
  };

  const btn = "brutal-press inline-flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase brutal-border";
  const style = (bg, fg) => ({ background: bg, color: fg, borderWidth: 3, boxShadow: "3px 3px 0 0 #000" });

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`share-${incident.id}`}>
      <span className="font-mono text-[10px] uppercase text-white/50 mr-1">Spread it:</span>
      <button onClick={copy} className={btn} style={style("#fff", "#000")} data-testid={`share-copy-${incident.id}`}>
        {copied ? <Check size={13} strokeWidth={3} /> : <Link2 size={13} strokeWidth={3} />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <a
        href={`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        className={btn}
        style={style("#000", "#fff")}
        data-testid={`share-x-${incident.id}`}
      >
        <XIcon /> Post
      </a>
      <a
        href={`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(text)}`}
        target="_blank"
        rel="noopener noreferrer"
        className={btn}
        style={style("#FF4500", "#fff")}
        data-testid={`share-reddit-${incident.id}`}
      >
        <RedditIcon /> Reddit
      </a>
      <button onClick={cover} disabled={rendering} className={`${btn} disabled:opacity-60`} style={style("#FFEA00", "#000")} data-testid={`share-cover-${incident.id}`}>
        <ImageDown size={13} strokeWidth={3} className={rendering ? "animate-bounce" : ""} />
        {rendering ? "Printing..." : "Cover PNG"}
      </button>
    </div>
  );
};
